/**
 * Base API client for frontend
 * Handles credentials, error parsing, and special status codes
 */

import { useUIStore } from '../stores/ui.store';

export interface ApiError {
  code: string;
  message: string;
  details?: Record<string, unknown>;
}

export interface ApiResponse<T> {
  data: T;
  meta?: Record<string, unknown>;
}

export interface FetchOptions extends Omit<RequestInit, 'body'> {
  body?: unknown;
  params?: Record<string, string | number | boolean | undefined>;
}

const API_BASE = '/api/v1';

/**
 * Constructs URL with query parameters
 */
function buildUrl(path: string, params?: Record<string, string | number | boolean | undefined>): string {
  const url = new URL(path, window.location.origin);

  if (params) {
    Object.entries(params).forEach(([key, value]) => {
      if (value !== undefined && value !== null && value !== '') {
        url.searchParams.append(key, String(value));
      }
    });
  }

  return url.toString();
}

/**
 * Typed fetch wrapper for API calls
 * - Prepends /api/v1 to path
 * - Includes credentials for httpOnly cookies
 * - Parses JSON responses
 * - Handles 401 (redirect to /login) and 429 (show upgrade prompt)
 */
export async function apiFetch<T>(
  path: string,
  options: FetchOptions = {}
): Promise<T> {
  const { body, params, headers: customHeaders, ...fetchOptions } = options;

  const url = buildUrl(`${API_BASE}${path}`, params);

  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...customHeaders,
  };

  const config: RequestInit = {
    ...fetchOptions,
    headers,
    credentials: 'include', // Required for httpOnly cookie auth
  };

  if (body !== undefined) {
    config.body = JSON.stringify(body);
  }

  const response = await fetch(url, config);

  // Handle 401 Unauthorized - redirect to login
  if (response.status === 401) {
    // Only redirect if not already on login page AND not just checking initial session
    if (!window.location.pathname.startsWith('/login') && path !== '/auth/me') {
      window.location.href = '/login';
    }
    throw new ApiClientError('Unauthorized', 401, 'UNAUTHORIZED');
  }

  // Handle 429 Rate Limited - trigger upgrade prompt
  if (response.status === 429) {
    const uiStore = useUIStore.getState();
    uiStore.showUpgrade('rate_limit');
    throw new ApiClientError('Rate limit exceeded', 429, 'RATE_LIMIT_EXCEEDED');
  }

  // Parse JSON response
  let data: unknown;
  const contentType = response.headers.get('content-type');

  if (contentType?.includes('application/json')) {
    try {
      data = await response.json();
    } catch {
      throw new ApiClientError('Failed to parse JSON response', response.status, 'PARSE_ERROR');
    }
  } else {
    data = await response.text();
  }

  // Handle error responses
  if (!response.ok) {
    const errorData = data as { error?: ApiError } | string;
    const apiError = typeof errorData === 'object' && errorData?.error
      ? errorData.error
      : { code: 'UNKNOWN_ERROR', message: String(data) };

    throw new ApiClientError(
      apiError.message,
      response.status,
      apiError.code,
      apiError.details
    );
  }

  return data as T;
}

/**
 * Custom error class for API errors
 */
export class ApiClientError extends Error {
  constructor(
    message: string,
    public readonly status: number,
    public readonly code: string,
    public readonly details?: Record<string, unknown>
  ) {
    super(message);
    this.name = 'ApiClientError';
  }
}

/**
 * Convenience methods for common HTTP verbs
 */
export const api = {
  get: <T>(path: string, params?: Record<string, string | number | boolean | undefined>) =>
    apiFetch<T>(path, { method: 'GET', params }),

  post: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'POST', body }),

  put: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PUT', body }),

  patch: <T>(path: string, body?: unknown) =>
    apiFetch<T>(path, { method: 'PATCH', body }),

  delete: <T>(path: string) =>
    apiFetch<T>(path, { method: 'DELETE' }),
};
